import { min, max, isEmpty, isObject, isBoolean } from 'lodash'
import { useState, useRef, ReactElement, ChangeEventHandler, KeyboardEventHandler, FocusEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { SpinnerIcon } from 'components/Icons'
import { Input } from 'components/Input'
import Dropdown from 'components/v2/Dropdown'
import EmptyState from 'components/v2/EmptyState'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Label, OptionalLabel } from 'components/v2/Label'
import List from 'components/v2/List'
import { Tag } from 'components/v2/Tag'
import { normalize, KEY_CODES } from 'utils/string'
import { ISelectTagsProps } from './types'

const Wrapper = styled.div<{ showError?: boolean; disabled?: boolean; small?: boolean; noPadding?: boolean }>`
  align-items: center;
  background-color: ${({ disabled }): string => (disabled ? '#F8F8F8' : '#ffffff')};
  border: 1px solid ${({ showError }): string => (showError ? '#FF627B' : '#EAE9E9')};
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: ${({ small, noPadding }): string => (noPadding ? '0px' : small ? '6px 8px' : '11px 12px')};
  width: auto;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'text')};

  &:hover,
  &:focus-within {
    border: 1px solid #717171;
  }
`

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #5d5c70;
`

const canDelete = (value: TSFixMe): boolean => {
  if (isObject(value)) {
    return !(value as { readOnly?: boolean })?.readOnly
  }

  return true
}

const SelectTags = ({
  id,
  label,
  values = [],
  readOnlyValues = [],
  items = [],
  onChange,
  onBlur,
  getTagLabel,
  placeholder,
  emptyMessage,
  limit = 1000,
  touched,
  errors,
  optional,
  loading,
  error,
  shouldShowItems,
  enableNewTags,
  ...inputProps
}: ISelectTagsProps): ReactElement => {
  let dropdownContent
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const [focusIndex, setFocusIndex] = useState(0)
  const [open, setOpen] = useState(false)
  const showError = Boolean(touched && errors)

  const toggleDropdown = (): void => {
    setOpen(!open)
  }

  const handleClickAway = (): void => {
    setOpen(false)
  }

  const filterItemsBy = (value: TSFixMe): TSFixMe[] => {
    return items.filter((item) => {
      const inputValue = normalize(value)
      const itemLabel = normalize(getTagLabel(item))
      return itemLabel.indexOf(inputValue) > -1
    })
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }): void => {
    if (!open) {
      setOpen(true)
    }

    setValue(value)
    onChange(values, value)
    setFocusIndex(0)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    if (enableNewTags && !isEmpty(value)) {
      handleSelectItem(value)
    }

    onBlur?.(event)
  }

  const handleSelectItem = (item: TSFixMe): void => {
    onChange([...values, item], '')

    setValue('')
    setFocusIndex(0)
    setOpen(false)
    inputRef.current?.focus()
  }

  const filteredItems = filterItemsBy(value).slice(0, limit)

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
    switch (e.keyCode) {
      case KEY_CODES.ARROW_UP:
        e.preventDefault()
        if (open) {
          setFocusIndex(max([0, focusIndex - 1]) ?? 0)
        }
        break
      case KEY_CODES.ARROW_DOWN:
        e.preventDefault()
        if (open) {
          setFocusIndex(min([filteredItems.length, focusIndex + 1]) ?? 0)
        } else {
          setOpen(true)
        }
        break
      case KEY_CODES.ENTER:
        if (open) {
          e.preventDefault()

          if (filteredItems.length > 0) {
            handleSelectItem(filteredItems[focusIndex])
          } else if (enableNewTags && !isEmpty(value)) {
            handleSelectItem(value)
          }
        }
        break
      case KEY_CODES.BACKSPACE:
        if (!value && !isEmpty(values) && canDelete(values.at(-1))) {
          const newValues = values.slice(0, values.length - 1)
          onChange(newValues, '')
        }
    }
  }

  const handleTagDelete = (tagIndex: number): void => {
    if (inputProps.disabled || inputProps.readOnly) return

    const newValues = values.filter((value, index) => {
      return index !== tagIndex
    })
    onChange(newValues, value)
  }

  if (isBoolean(shouldShowItems) && !shouldShowItems) {
    dropdownContent = (
      <EmptyState.Container>
        <FormattedMessage
          id="autocomplete.minCharacters.description"
          defaultMessage="Please enter at least 3 characters to see the results"
        />
      </EmptyState.Container>
    )
  } else if (loading) {
    dropdownContent = (
      <EmptyState.Container>
        <StyledSpinnerIcon />
      </EmptyState.Container>
    )
  } else if (error) {
    dropdownContent = (
      <EmptyState.Container>
        <FormattedMessage id="selectTags.error.label" defaultMessage="There was an error while loading the items" />
      </EmptyState.Container>
    )
  } else if (isEmpty(filteredItems)) {
    dropdownContent = (
      <EmptyState.Container>
        {emptyMessage || <FormattedMessage id="form.empty.label" defaultMessage="No items found" />}
      </EmptyState.Container>
    )
  } else {
    dropdownContent = (
      <List.Container>
        {filteredItems.map((item, index) => {
          return (
            <List.Item
              key={index}
              onClick={(e: TSFixMe): void => {
                e.preventDefault()
                e.stopPropagation()
                handleSelectItem(item)
              }}
              isFocused={focusIndex === index}
              action
            >
              {getTagLabel(item)}
            </List.Item>
          )
        })}
      </List.Container>
    )
  }

  return (
    <Dropdown.Wrapper onClickAway={handleClickAway} form>
      <label>
        {label && (
          <Label>
            {label}{' '}
            {optional && (
              <OptionalLabel>
                <FormattedMessage id="form.optional.label" defaultMessage="Optional" />
              </OptionalLabel>
            )}
          </Label>
        )}
        <Wrapper disabled={inputProps.disabled || inputProps.readOnly} small={values.length > 0} showError={showError}>
          {values.map((tag, index) => {
            const isValueReadOnly = readOnlyValues.some((value) => {
              return getTagLabel(value) === getTagLabel(tag)
            })

            return (
              <Tag
                key={index}
                onDelete={handleTagDelete.bind(this, index)}
                isDeleteDisabled={inputProps.disabled || inputProps.readOnly || isValueReadOnly}
              >
                {getTagLabel(tag)}
              </Tag>
            )
          })}
          <Input
            id={id}
            ref={inputRef}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={toggleDropdown}
            noBorder
            noPadding
            hasDynamicWidth
            {...inputProps}
          />
        </Wrapper>
        {showError && !open && <ErrorMessage>{errors}</ErrorMessage>}
        <Dropdown.Content open={open} noIndicator left form>
          {dropdownContent}
        </Dropdown.Content>
      </label>
    </Dropdown.Wrapper>
  )
}

export { SelectTags }
